"use client";

import { useEffect } from "react";
import { useRouter, useParams } from "next/navigation";
import { useForm, Controller } from "react-hook-form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { RichHtmlEditor } from "@admin/_components/rich-html-editor";

interface AssignmentFormValues {
  assignment_title: string;
  assignment_lesson: string;
  priority: string;
  assignment_description: string;
  status: boolean;
}

const BASE_URL = process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";

function LessonSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data: session } = useSession();
  const { data: opts, isLoading } = useQuery({
    queryKey: ["admin/fetch-lesson-list"],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}admin/fetch-lesson-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ condition: {}, limit: 200, skip: 0 }),
      });
      const json = await res.json();
      return (json.results?.res ?? json.results ?? json.dt?.res ?? []) as { _id: string; lesson_name: string }[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={isLoading}
      className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none disabled:opacity-50"
      style={{ borderColor: "hsl(240 20% 22%)" }}>
      <option value="">{isLoading ? "Loading…" : "Select lesson"}</option>
      {(opts ?? []).map((l) => <option key={l._id} value={l._id}>{l.lesson_name}</option>)}
    </select>
  );
}

export function AssignmentForm() {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const isEdit = !!id;
  const { data: session } = useSession();

  const { control, handleSubmit, reset, register, formState: { errors } } = useForm<AssignmentFormValues>({
    defaultValues: { assignment_title: "", assignment_lesson: "", priority: "", assignment_description: "", status: true },
  });

  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["training-centre/assignment-preview", id],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}training-centre/assignment-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      // Angular reads from response.response.res
      return data?.response?.res ?? data?.response?.results?.res ?? data?.results?.res ?? data?.results ?? data;
    },
    enabled: isEdit && !!session?.token,
  });

  useEffect(() => {
    if (!record) return;
    const r = record as Record<string, unknown>;
    reset({
      assignment_title: String(r.assignment_title ?? ""),
      assignment_lesson: String(r.assignment_lesson ?? ""),
      priority: String(r.priority ?? ""),
      assignment_description: String(r.assignment_description ?? ""),
      status: r.status === 1 || r.status === true,
    });
  }, [record, reset]);

  const addMutation = useApiPost("training-centre/assginment-add", { invalidateKeys: ["training-centre/assignment-list", "training-centre/assignment-list-count"] });
  const editMutation = useApiPost("training-centre/assignment-edit", { invalidateKeys: ["training-centre/assignment-list", "training-centre/assignment-list-count"] });
  const mutation = isEdit ? editMutation : addMutation;

  async function onSubmit(values: AssignmentFormValues) {
    try {
      const body: Record<string, unknown> = {
        assignment_title: values.assignment_title,
        assignment_lesson: values.assignment_lesson,
        priority: values.priority ? parseFloat(values.priority) : 0,
        assignment_description: values.assignment_description,
        status: values.status ? 1 : 0,
      };
      if (isEdit) body._id = id;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Assignment updated" : "Assignment created");
      router.push("/admin/training/assignments");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96 rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => router.back()}><ArrowLeft className="size-4" /></Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <h1 className="text-xl font-semibold">{isEdit ? "Edit Assignment" : "Add Assignment"}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Assignment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            <div className="space-y-1.5">
              <Label>Assignment Title <span className="text-destructive">*</span></Label>
              <Input {...register("assignment_title", { required: "Title is required" })} placeholder="Assignment title" />
              {errors.assignment_title && <p className="text-xs text-destructive">{errors.assignment_title.message}</p>}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Lesson</Label>
                <Controller name="assignment_lesson" control={control}
                  render={({ field }) => <LessonSelect value={field.value} onChange={field.onChange} />}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Input type="number" {...register("priority")} placeholder="1" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Description <span className="text-destructive">*</span></Label>
              <Controller name="assignment_description" control={control} rules={{ required: "Description is required" }}
                render={({ field }) => <RichHtmlEditor value={field.value} onChange={field.onChange} />}
              />
              {errors.assignment_description && <p className="text-xs text-destructive">{errors.assignment_description.message}</p>}
            </div>

            <Controller name="status" control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div role="switch" aria-checked={field.value} tabIndex={0}
                    onClick={() => field.onChange(!field.value)}
                    onKeyDown={(e) => e.key === " " && field.onChange(!field.value)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${field.value ? "bg-orange-500" : "bg-muted"}`}>
                    <span className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform ${field.value ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                  <span className="text-sm">Active</span>
                </label>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <button type="submit" disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 btn-orange">
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mutation.isPending ? "Saving…" : isEdit ? "Update Assignment" : "Create Assignment"}
          </button>
          <Button type="button" variant="outline" onClick={() => router.back()}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
